import { FC, useMemo } from 'react';

import { XIcon } from '@heroicons/react/outline';
import { KakaoShareCallback } from '@shared/api/giftCoupon/giftCoupon.interface';

import { fmtTime } from './constants';

/**
 * 모달이 필요로 하는 최소 필드만 정의한 generic 형태.
 * `GiftCouponSummary`, `GiftCouponForRedemption`, `GiftCoupon` 어떤 변형이든
 * `_id`, `code`, `seq`, `kakaoShareCallbacks` 만 있으면 그대로 사용할 수 있다.
 */
export interface CouponWithKakaoCallbacks {
  _id: string;
  code: string;
  seq: number;
  kakaoShareCallbacks?: KakaoShareCallback[];
}

interface KakaoShareCallbackModalProps {
  coupon: CouponWithKakaoCallbacks;
  onClose: () => void;
}

const chatTypeLabel: Record<string, string> = {
  MemoChat: '나에게 보내기',
  DirectChat: '1:1 채팅',
  MultiChat: '단톡방',
  OpenMultiChat: '오픈 단톡방',
};

const chatTypeBadgeStyle: Record<string, string> = {
  MemoChat: 'bg-slate-100 text-slate-600',
  DirectChat: 'bg-blue-100 text-blue-700',
  MultiChat: 'bg-purple-100 text-purple-700',
  OpenMultiChat: 'bg-pink-100 text-pink-700',
};

interface ChatGroup {
  /** `${chatType}:${hashChatId}` — 그룹 식별 키 */
  key: string;
  hashChatId: string;
  chatType: string;
  count: number;
  /** 전송 시각이 빠른 → 늦은 순으로 정렬된 콜백 */
  callbacks: KakaoShareCallback[];
}

/**
 * 콜백을 `(chatType, hashChatId)` 기준으로 그룹핑.
 * - 동일 채팅방에 여러 번 보낸 경우 한 묶음으로 묶되, 그 안의 모든 전송 시각은 그대로 노출.
 * - hashChatId 가 빈 값(=식별 불가)이면 콜백 ID 자체를 키로 써서 따로 표시.
 */
const groupByChatRoom = (callbacks: KakaoShareCallback[]): ChatGroup[] => {
  const map = new Map<string, ChatGroup>();

  for (const cb of callbacks) {
    const hash = cb.hashChatId || '';
    const key = hash ? `${cb.chatType ?? ''}:${hash}` : `__no-hash__:${cb._id}`;
    const existing = map.get(key);
    if (!existing) {
      map.set(key, {
        key,
        hashChatId: hash,
        chatType: cb.chatType ?? '',
        count: 1,
        callbacks: [cb],
      });
      continue;
    }
    existing.count += 1;
    existing.callbacks.push(cb);
  }

  const groups = Array.from(map.values());
  for (const g of groups) {
    g.callbacks.sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    );
  }

  // 마지막 전송이 최신인 채팅방을 위로
  return groups.sort((a, b) => {
    const aLast = a.callbacks[a.callbacks.length - 1]?.createdAt ?? '';
    const bLast = b.callbacks[b.callbacks.length - 1]?.createdAt ?? '';
    return new Date(bLast).getTime() - new Date(aLast).getTime();
  });
};

/**
 * 한 쿠폰의 카카오톡 공유 웹훅 역참조(`kakaoShareCallbacks`) 목록을 보여주는 모달.
 * - `(chatType, hashChatId)` 기준으로 채팅방 단위로 그룹핑한 뒤,
 *   각 채팅방 카드 내부에 전송 시각을 모두 시간 순으로 나열한다.
 */
export const KakaoShareCallbackModal: FC<KakaoShareCallbackModalProps> = ({
  coupon,
  onClose,
}) => {
  const callbacks = useMemo<KakaoShareCallback[]>(
    () => coupon.kakaoShareCallbacks ?? [],
    [coupon.kakaoShareCallbacks],
  );
  const groups = useMemo(() => groupByChatRoom(callbacks), [callbacks]);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative flex max-h-[80vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="flex shrink-0 items-center justify-between px-5 py-3">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-base font-bold text-gray-900">카카오톡 공유 이력</h3>
            <span className="text-xs text-gray-500">
              <span className="font-mono">
                {coupon.code}-{coupon.seq}
              </span>
              <span className="mx-1 text-gray-300">·</span>
              <span className="font-semibold text-kakao-brown">
                {groups.length}개 채팅방
              </span>
              <span className="mx-1 text-gray-300">·</span>
              <span className="text-gray-500">총 {callbacks.length}건 전송</span>
            </span>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <XIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-3 overflow-auto px-4 pb-4">
          {groups.length === 0 ? (
            <div className="flex h-32 items-center justify-center text-sm text-gray-400">
              아직 카카오톡 공유 웹훅 기록이 없습니다.
            </div>
          ) : (
            groups.map((g, idx) => {
              const typeLabel = chatTypeLabel[g.chatType] ?? g.chatType ?? '알 수 없음';
              const badgeClass =
                chatTypeBadgeStyle[g.chatType] ?? 'bg-gray-100 text-gray-600';
              return (
                <div
                  key={g.key}
                  className="overflow-hidden rounded-lg border border-gray-200 bg-white"
                >
                  <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 bg-gray-50 px-3 py-2">
                    <span className="text-xs font-semibold text-gray-400">
                      #{idx + 1}
                    </span>
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${badgeClass}`}
                    >
                      {typeLabel}
                    </span>
                    <span
                      className={`inline-flex h-5 min-w-[28px] items-center justify-center rounded-full px-1.5 text-[10px] font-bold ${
                        g.count > 1
                          ? 'bg-kakao-yellow text-kakao-brown'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {g.count}회
                    </span>
                    <span
                      className="ml-1 max-w-[260px] truncate font-mono text-[10px] text-gray-500"
                      title={g.hashChatId || '(해시 없음)'}
                    >
                      {g.hashChatId || '(해시 없음)'}
                    </span>
                  </div>

                  <table className="w-full text-xs">
                    <thead className="bg-white text-left text-gray-400">
                      <tr>
                        <th className="whitespace-nowrap px-3 py-1.5 font-medium">
                          순번
                        </th>
                        <th className="whitespace-nowrap px-3 py-1.5 font-medium">
                          전송 시각
                        </th>
                        <th className="whitespace-nowrap px-3 py-1.5 font-medium">
                          도메인 URL
                        </th>
                        <th className="whitespace-nowrap px-3 py-1.5 font-medium">앱</th>
                        <th className="whitespace-nowrap px-3 py-1.5 font-medium">
                          리소스ID
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {g.callbacks.map((cb, i) => {
                        const domainUrl = cb.args?.domainUrl;
                        return (
                          <tr key={cb._id} className="hover:bg-gray-50">
                            <td className="whitespace-nowrap px-3 py-1.5 text-gray-400">
                              {i + 1}
                            </td>
                            <td className="whitespace-nowrap px-3 py-1.5 text-gray-700">
                              {fmtTime(cb.createdAt)}
                            </td>
                            <td
                              className="max-w-[260px] truncate px-3 py-1.5 text-[11px]"
                              title={domainUrl || ''}
                            >
                              {domainUrl ? (
                                <a
                                  href={domainUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-indigo-600 hover:underline"
                                >
                                  {domainUrl}
                                </a>
                              ) : (
                                <span className="text-gray-300">-</span>
                              )}
                            </td>
                            <td className="whitespace-nowrap px-3 py-1.5 text-gray-500">
                              {cb.appName || '-'}
                            </td>
                            <td
                              className="max-w-[160px] truncate px-3 py-1.5 font-mono text-[10px] text-gray-400"
                              title={cb.kakaoResourceId || ''}
                            >
                              {cb.kakaoResourceId || '-'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })
          )}

          {callbacks.length > 0 && (
            <p className="pt-1 text-[11px] text-gray-400">
              * `채팅방 해시(hashChatId)` + `대화방 종류` 가 같은 콜백을 한 채팅방으로
              묶어 표시합니다. 같은 채팅방 안에서는 모든 전송 시각을 시간 순으로 그대로
              나열합니다.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
